'use client'

import { RefObject } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { gsapSetup } from './gsapSetup'
import { useReducedMotion } from './useReducedMotion'

/**
 * Problem section — pinned cinematic scene.
 *
 * Uses GSAP's own pin (not CSS sticky) so it's immune to ancestor overflow
 * quirks. One master scrubbed timeline drives all stages and per-stage
 * reveals; each stage owns 1 unit of timeline, pinned for 1 viewport height
 * of scroll. The backdrop darkens across the whole chapter.
 *
 * Layout contract (see Problem.tsx):
 *   <section id="problem" ref={ref}>
 *     <div data-viewport>
 *       <div data-darken />
 *       <div data-stage="0">…</div>   (has [data-anim] and [data-countup])
 *       <div data-stage="1">…</div>
 *       …
 *     </div>
 *   </section>
 */
export function useProblemScene(ref: RefObject<HTMLElement | null>) {
  const reduced = useReducedMotion()

  useGSAP(
    () => {
      gsapSetup()

      const root = ref.current
      if (!root) return

      const viewport = root.querySelector<HTMLElement>('[data-viewport]')
      const darken   = root.querySelector<HTMLElement>('[data-darken]')
      const stages   = gsap.utils.toArray<HTMLElement>('[data-stage]', root)
      if (!viewport || stages.length === 0) return

      // ── Reduced motion: render stages stacked, no pin, no scrub. ────────
      if (reduced) {
        gsap.set(viewport, { height: 'auto' })
        stages.forEach((s) => {
          gsap.set(s, { position: 'relative', autoAlpha: 1, y: 0 })
        })
        root.querySelectorAll<HTMLElement>('[data-countup]').forEach((n) => {
          const v = n.dataset.countup
          if (v) n.textContent = v
        })
        return
      }

      const splits: SplitText[] = []
      const N = stages.length

      // Timeline-unit budget per stage. The sub-reveal block (text sliding
      // in, split cascade) occupies ~0.40 units; the cross-fade to the next
      // stage occupies 0.25 units at the end. Everything between is a hold,
      // i.e. the user can linger and read. STAGE_SPAN = 1.4 gives roughly
      // 0.75 units of hold ≈ 75% of a viewport of scroll before the next
      // fade begins.
      const STAGE_SPAN  = 1.4
      const CROSSFADE   = 0.25
      const totalUnits  = N * STAGE_SPAN

      // ── Initial states ───────────────────────────────────────────────────
      stages.forEach((s, i) => {
        gsap.set(s, {
          autoAlpha: i === 0 ? 1 : 0,
          y:         i === 0 ? 0 : 60,
          willChange: 'opacity, transform',
        })
      })

      if (darken) gsap.set(darken, { opacity: 0 })

      // Prepare per-stage sub-reveal targets.
      stages.forEach((stage) => {
        const items = gsap.utils.toArray<HTMLElement>('[data-anim]', stage)
        items.forEach((node) => {
          if (node.dataset.anim === 'split') {
            const split = new SplitText(node, { type: 'words,chars' })
            splits.push(split)
            // Parent h2/h3 must be visible — the reveal animation targets
            // split.words (children), not the parent. Override the SSR
            // FOUC guard CSS rule (#problem [data-anim] { opacity: 0 })
            // with an explicit opacity:1 here. Words stay hidden.
            gsap.set(node, { opacity: 1 })
            gsap.set(split.words, { yPercent: 120, autoAlpha: 0 })
          } else {
            gsap.set(node, { y: 56, autoAlpha: 0 })
          }
        })
      })

      // ── Master scrubbed + pinned timeline ────────────────────────────────
      // 1 timeline unit = 1 viewport height of scroll. Pin for the whole
      // span so the section holds while the timeline plays.
      const master = gsap.timeline({
        scrollTrigger: {
          trigger: viewport,
          start: 'top top',
          end:  `+=${totalUnits * 100}%`,
          pin: true,
          pinSpacing: true,
          scrub: 0.5,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
        defaults: { ease: 'none' },
      })

      // Darken across the whole pin.
      if (darken) {
        master.to(darken, { opacity: 0.82, duration: totalUnits }, 0)
      }

      // For each stage: sub-reveal at the start of the slot, hold, then
      // cross-fade to the next stage at the very end of the slot.
      // Stage 0 (entry): items start revealing immediately at offset 0
      // with a tight stagger so the section never reads as empty after
      // the pin engages. Later stages keep a small breath before reveal
      // so the cross-fade has time to settle.
      for (let i = 0; i < N; i++) {
        const stage     = stages[i]
        const items     = gsap.utils.toArray<HTMLElement>('[data-anim]', stage)
        const slotStart = i * STAGE_SPAN
        const slotEnd   = slotStart + STAGE_SPAN

        // Faster entrance for the first stage; gentler for the rest.
        const baseOffset = i === 0 ? 0     : 0.06
        const stagger    = i === 0 ? 0.045 : 0.07
        const itemDur    = i === 0 ? 0.20  : 0.26
        const splitDur   = i === 0 ? 0.24  : 0.30

        // Sub-reveals: complete within the first ~0.40 units of the slot.
        items.forEach((node, j) => {
          const offset = slotStart + baseOffset + j * stagger
          if (node.dataset.anim === 'split') {
            const split = splits.find((s) => s.elements[0] === node)
            if (split) {
              master.to(split.words, {
                yPercent: 0,
                autoAlpha: 1,
                duration: splitDur,
                stagger: 0.025,
              }, offset)
            }
          } else {
            master.to(node, {
              y: 0,
              autoAlpha: 1,
              duration: itemDur,
            }, offset)
          }
        })

        // Cross-fade to the next stage at the end of the slot (hold gap
        // fills everything between sub-reveals and this cross-fade).
        if (i < N - 1) {
          const fadePos = slotEnd - CROSSFADE
          master.to(stage, {
            autoAlpha: 0,
            y: -50,
            duration: CROSSFADE,
          }, fadePos)
          master.to(stages[i + 1], {
            autoAlpha: 1,
            y: 0,
            duration: CROSSFADE,
          }, fadePos)
        }
      }

      // ── Count-ups (one-shot, NOT tied to scroll) ─────────────────────────
      // Each numeral counts once when its stage becomes active and stops at
      // the final value. Scrolling back does not restart it; scrolling away
      // does not reset it.
      //
      // We piggy-back on the master timeline via `.call()` instead of using
      // separate ScrollTriggers: the master's own ScrollTrigger already
      // knows the pin geometry, so its timeline positions are reliable.
      // A `fired` flag guards against scrub-direction re-triggers.
      root.querySelectorAll<HTMLElement>('[data-countup]').forEach((node) => {
        const end = Number(node.dataset.countup)
        if (!Number.isFinite(end)) return
        const stage = node.closest<HTMLElement>('[data-stage]')
        if (!stage) return
        const i = stages.indexOf(stage)
        if (i < 0) return

        node.textContent = '0'
        let fired = false

        master.call(
          () => {
            if (fired) return
            fired = true
            const obj = { v: 0 }
            gsap.to(obj, {
              v: end,
              duration: 1.8,
              ease: 'power2.out',
              onUpdate: () => {
                node.textContent = Math.round(obj.v).toString()
              },
            })
          },
          undefined,
          // Fire once the stage's sub-reveals are roughly done — 0.3 units
          // into the slot.
          i * STAGE_SPAN + 0.30,
        )
      })

      ScrollTrigger.refresh()

      return () => {
        splits.forEach((s) => s.revert())
      }
    },
    { scope: ref, dependencies: [reduced], revertOnUpdate: true },
  )
}
